import { useMemo, useState } from 'react';
import { View, Text, ScrollView, Pressable, Switch, useWindowDimensions } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DollarSign, Users, CheckCircle, XCircle, Clock } from 'lucide-react-native';

// UI Components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/Card';
import { Badge } from './ui/Badge';
import { AppSelect } from '@/components/ui/AppSelect';
import { Skeleton } from './ui/Skeleton';

// Hooks and Utils
import { useToast } from '../hooks/useToast';
import { isUnauthorizedError } from '../lib/authUtils';
import { format } from 'date-fns';
import * as Linking from 'expo-linking';
import { getAuthHref } from '@/lib/routes';
import { colors, typography } from '@/lib/theme';
import { useTRPC, useTRPCClient } from '@/lib/trpc';

interface PaymentLeader {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  buyInStatus: string;
  points: number;
}

interface WeeklyLeader {
  weekId: string;
  weekNumber: number;
  topMember?: PaymentLeader | null;
  topPaidMember?: PaymentLeader | null;
}

interface Week {
  id: string;
  leagueWeekId: string;
  label: string;
  displayLabel: string;
  weekNumber: number | null;
  startsAt: string | null;
  endsAt: string | null;
  roundKey?: string | null;
  roundLabel?: string | null;
}

interface LeagueSeasonWeeksPayload {
  resolvedSeasonId: string;
  weeks: Week[];
}

interface PaymentManagementProps {
  leagueId: string;
  seasonId?: string | null;
}

const redirectToAuth = () => {
  const authUrl = Linking.createURL(String(getAuthHref()));
  if (typeof window !== 'undefined') {
    window.location.href = authUrl;
    return;
  }
  Linking.openURL(authUrl);
};

export default function PaymentManagement({ leagueId, seasonId }: PaymentManagementProps) {
  const { width: windowWidth } = useWindowDimensions();
  const isCompact = windowWidth < 720;
  const { toast } = useToast();
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();
  const [hidePaidWeeks, setHidePaidWeeks] = useState(true);
  const [showMemberStatus, setShowMemberStatus] = useState(false);

  // Fetch payment summary
  const { data: paymentSummary, isLoading: loadingSummary } = useQuery(
    trpc.leagues.paymentSummary.queryOptions(
      {
        leagueId,
        seasonId: seasonId ?? undefined,
      },
      {
        enabled: !!seasonId,
      },
    ),
  );

  // Fetch league members
  const { data: members = [], isLoading: loadingMembers } = useQuery(
    trpc.leagues.members.queryOptions(
      {
        leagueId,
        seasonId: seasonId ?? undefined,
      },
      {
        enabled: !!seasonId,
      },
    ),
  );

  // Fetch season weeks so tournament seasons can display round labels.
  const seasonWeeksQuery = useQuery(
    trpc.leagues.seasonWeeks.queryOptions(
      {
        leagueId,
        seasonId: seasonId ?? undefined,
      },
      {
        enabled: !!leagueId && !!seasonId,
      },
    ),
  );
  const seasonWeeks = seasonWeeksQuery.data as LeagueSeasonWeeksPayload | undefined;

  // Update buy-in status mutation
  const updateBuyInStatusMutation = useMutation({
    mutationFn: async ({ userId, status }: { userId: string; status: 'paid' | 'unpaid' | 'opted-out' }) => {
      return trpcClient.leagues.updateMemberBuyInStatus.mutate({
        leagueId,
        userId,
        status,
        seasonId: seasonId ?? undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.leagues.paymentSummary.queryKey({
          leagueId,
          seasonId: seasonId ?? undefined,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.leagues.members.queryKey({
          leagueId,
          seasonId: seasonId ?? undefined,
        }),
      });
      toast.show({
        type: 'success',
        text1: 'Buy-in Status Updated',
        text2: 'Member buy-in status has been updated successfully.',
      });
    },
    onError: (error: any) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          type: 'error',
          text1: 'Unauthorized',
          text2: 'You are logged out. Logging in again...',
        });
        setTimeout(() => {
          redirectToAuth();
        }, 500);
        return;
      }
      toast.show({
        type: 'error',
        text1: 'Error',
        text2: 'Failed to update buy-in status. Please try again.',
      });
    },
  });

  // Update week payment status mutation
  const updateWeekStatusMutation = useMutation({
    mutationFn: async ({ weekId, status }: { weekId: string; status: 'paid' | 'unpaid' }) => {
      return trpcClient.leagues.updateWeekPaymentStatus.mutate({
        leagueId,
        weekId,
        status,
        seasonId: seasonId ?? undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.leagues.paymentSummary.queryKey({
          leagueId,
          seasonId: seasonId ?? undefined,
        }),
      });
      toast.show({
        type: 'success',
        text1: 'Week Payment Status Updated',
        text2: 'Week payment status has been updated successfully.',
      });
    },
    onError: (error: any) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          type: 'error',
          text1: 'Unauthorized',
          text2: 'You are logged out. Logging in again...',
        });
        setTimeout(() => {
          redirectToAuth();
        }, 500);
        return;
      }
      toast.show({
        type: 'error',
        text1: 'Error',
        text2: 'Failed to update week payment status. Please try again.',
      });
    },
  });

  const updateSeasonStatusMutation = useMutation({
    mutationFn: async ({ status }: { status: 'paid' | 'unpaid' }) => {
      return trpcClient.leagues.updateSeasonPaymentStatus.mutate({
        leagueId,
        status,
        seasonId: seasonId ?? undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.leagues.paymentSummary.queryKey({
          leagueId,
          seasonId: seasonId ?? undefined,
        }),
      });
      toast.show({
        type: 'success',
        text1: 'Season Payment Status Updated',
        text2: 'Season payout status has been updated successfully.',
      });
    },
    onError: (error: any) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          type: 'error',
          text1: 'Unauthorized',
          text2: 'You are logged out. Logging in again...',
        });
        setTimeout(() => {
          redirectToAuth();
        }, 500);
        return;
      }
      toast.show({
        type: 'error',
        text1: 'Error',
        text2: 'Failed to update season payment status. Please try again.',
      });
    },
  });

  const weekStatusMap = useMemo(() => {
    const map = new Map<string, string>();
    paymentSummary?.weeklyStatuses.forEach((status) => {
      map.set(status.weekId, status.status);
    });
    return map;
  }, [paymentSummary?.weeklyStatuses]);

  const weeklyLeaderMap = useMemo(() => {
    const map = new Map<string, WeeklyLeader>();
    paymentSummary?.weeklyLeaders?.forEach((leader) => {
      map.set(leader.weekId, leader);
    });
    return map;
  }, [paymentSummary?.weeklyLeaders]);

  const payoutWeekIds = useMemo(() => {
    return new Set(paymentSummary?.weeklyStatuses?.map((status) => status.weekId) ?? []);
  }, [paymentSummary?.weeklyStatuses]);

  const showWeeklyPayments = (paymentSummary?.weeklyBuyInCents ?? 0) > 0;
  const showSeasonPayout = (paymentSummary?.seasonBuyInCents ?? 0) > 0;
  const duesWeekCount = payoutWeekIds.size;
  const duesPerUser =
    ((paymentSummary?.weeklyBuyInCents ?? 0) / 100) * duesWeekCount + (paymentSummary?.seasonBuyInCents ?? 0) / 100;

  const visibleWeeks = useMemo(() => {
    const weeks = seasonWeeks?.weeks ?? [];
    const payoutWeeks = weeks.filter((week) => payoutWeekIds.has(week.id));
    if (hidePaidWeeks) {
      return payoutWeeks.filter((week) => weekStatusMap.get(week.id) !== 'paid');
    }
    return payoutWeeks;
  }, [seasonWeeks?.weeks, hidePaidWeeks, weekStatusMap, payoutWeekIds]);

  const formatLeaderName = (leader?: PaymentLeader | null) => {
    if (!leader) return '';
    const fullName = `${leader.firstName || ''} ${leader.lastName || ''}`.trim();
    return fullName || leader.email;
  };

  const now = Date.now();
  const isWeekCompleted = (week: Week) => {
    if (!week.endsAt) {
      return false;
    }
    return new Date(week.endsAt).getTime() < now;
  };

  const getBuyInStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <View style={styles.badgeContainer}>
            <CheckCircle size={12} color={colors.success[700]} style={styles.badgeIcon} />
            <Badge variant="default" style={styles.successBadge}>
              <Text style={styles.badgeTextPaid}>Paid</Text>
            </Badge>
          </View>
        );
      case 'unpaid':
        return (
          <View style={styles.badgeContainer}>
            <XCircle size={12} color={colors.error[600]} style={styles.badgeIcon} />
            <Badge variant="destructive">
              <Text style={styles.badgeTextUnpaid}>Unpaid</Text>
            </Badge>
          </View>
        );
      case 'opted-out':
        return (
          <View style={styles.badgeContainer}>
            <Clock size={12} color={colors.text.secondary} style={styles.badgeIcon} />
            <Badge variant="secondary">
              <Text style={styles.badgeTextOptedOut}>Opted Out</Text>
            </Badge>
          </View>
        );
      default:
        return (
          <Badge variant="secondary">
            <Text>Unknown</Text>
          </Badge>
        );
    }
  };

  const getWeekStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <View style={styles.badgeContainer}>
            <CheckCircle size={12} color={colors.success[700]} style={styles.badgeIcon} />
            <Badge variant="default" style={styles.successBadge}>
              <Text style={styles.badgeTextPaid}>Paid</Text>
            </Badge>
          </View>
        );
      case 'unpaid':
        return (
          <View style={styles.badgeContainer}>
            <XCircle size={12} color={colors.error[600]} style={styles.badgeIcon} />
            <Badge variant="destructive">
              <Text style={styles.badgeTextUnpaid}>Unpaid</Text>
            </Badge>
          </View>
        );
      default:
        return (
          <Badge variant="secondary">
            <Text>Not Set</Text>
          </Badge>
        );
    }
  };

  if (!seasonId || loadingSummary || loadingMembers || seasonWeeksQuery.isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <Skeleton style={styles.skeletonTitle} />
        <View style={styles.skeletonCards}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} style={styles.skeletonCard} />
          ))}
        </View>
        <Skeleton style={styles.skeletonContent} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <DollarSign size={24} color={colors.text.primary} />
        <Text style={[styles.title, isCompact && styles.titleCompact]}>Payment Management</Text>
      </View>

      {/* Payment Summary Cards */}
      {paymentSummary && (
        <View style={[styles.summaryCards, isCompact && styles.summaryCardsCompact]}>
          <Card style={[styles.summaryCard, isCompact && styles.summaryCardCompact]}>
            <CardContent style={styles.cardContent}>
              <View style={styles.cardHeader}>
              <Text style={styles.cardLabel}>Total Season Pot</Text>
                <DollarSign size={16} color={colors.text.secondary} />
              </View>
              <Text style={styles.cardValue}>${paymentSummary.totalPot.toFixed(2)}</Text>
              <Text style={styles.cardSubtext}>Based on active members only</Text>
            </CardContent>
          </Card>

          <Card style={[styles.summaryCard, isCompact && styles.summaryCardCompact]}>
            <CardContent style={styles.cardContent}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardLabel}>Dues per Member</Text>
                <DollarSign size={16} color={colors.text.secondary} />
              </View>
              <Text style={styles.cardValue}>${duesPerUser.toFixed(2)}</Text>
              <Text style={styles.cardSubtext}>
                Weekly buy-in x {duesWeekCount} + season buy-in
              </Text>
            </CardContent>
          </Card>

          <Card style={[styles.summaryCard, isCompact && styles.summaryCardCompact]}>
            <CardContent style={styles.cardContent}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardLabel}>Active Members</Text>
                <Users size={16} color={colors.text.secondary} />
              </View>
              <Text style={styles.cardValue}>{paymentSummary.activeMembersCount}</Text>
              <Text style={styles.cardSubtext}>Participating in payouts</Text>
            </CardContent>
          </Card>

          <Card style={[styles.summaryCard, isCompact && styles.summaryCardCompact]}>
            <CardContent style={styles.cardContent}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardLabel}>Opted Out</Text>
                <XCircle size={16} color={colors.text.secondary} />
              </View>
              <Text style={styles.cardValue}>{paymentSummary.optedOutMembersCount}</Text>
              <Text style={styles.cardSubtext}>Not participating</Text>
            </CardContent>
          </Card>
        </View>
      )}

      {/* Member Buy-in Status */}
      <Card style={styles.section}>
        <CardHeader>
          <View style={styles.sectionHeaderRow}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={showMemberStatus ? 'Hide member list' : 'Show member list'}
              onPress={() => setShowMemberStatus((prev) => !prev)}
              style={styles.arrowButton}
            >
              <Text style={styles.arrowButtonText}>{showMemberStatus ? 'V' : '>'}</Text>
            </Pressable>
            <View style={styles.sectionHeaderText}>
              <CardTitle>
                <Text style={styles.sectionTitle}>Member Buy-in Status</Text>
              </CardTitle>
              <CardDescription>
                <Text style={styles.sectionDescription}>
                  Manage individual member buy-in status. Opted-out members are excluded from pot calculations.
                </Text>
              </CardDescription>
            </View>
          </View>
        </CardHeader>
        <CardContent>
          {showMemberStatus ? (
            <View style={styles.table}>
              {/* Table Header */}
              <View style={[styles.tableHeader, isCompact && styles.tableHeaderCompact]}>
                <Text style={[styles.tableHeaderText, styles.memberColumn]}>Member</Text>
                <Text style={[styles.tableHeaderText, styles.statusColumn]}>Status</Text>
                <Text style={[styles.tableHeaderText, styles.actionColumn]}>Update</Text>
              </View>

              {/* Table Rows */}
              {members.map((member) => (
                <View key={member.league_members.id} style={[styles.tableRow, isCompact && styles.tableRowCompact]}>
                  <View style={[styles.memberColumn, isCompact && styles.tableColumnCompact]}>
                    <Text style={styles.memberName}>
                      {member.users.firstName} {member.users.lastName}
                    </Text>
                    <Text style={styles.memberEmail}>{member.users.email}</Text>
                  </View>

                  {isCompact && <Text style={styles.compactFieldLabel}>STATUS</Text>}
                  <View style={[styles.statusColumn, isCompact && styles.statusColumnCompact]}>
                    {getBuyInStatusBadge(member.league_members.buyInStatus)}
                  </View>

                  {isCompact && <Text style={styles.compactFieldLabel}>UPDATE STATUS</Text>}
                  <View style={[styles.actionColumn, isCompact && styles.actionColumnCompact]}>
                    <AppSelect
                      value={member.league_members.buyInStatus}
                      onChange={(status) =>
                        updateBuyInStatusMutation.mutate({
                          userId: member.league_members.userId,
                          status: status as 'paid' | 'unpaid' | 'opted-out',
                        })
                      }
                      options={[
                        { value: 'paid', label: 'Paid' },
                        { value: 'unpaid', label: 'Unpaid' },
                        { value: 'opted-out', label: 'Opted Out' },
                      ]}
                    />
                  </View>
                </View>
              ))}
            </View>
          ) : null}
        </CardContent>
      </Card>

      {showWeeklyPayments ? (
        <Card style={styles.section}>
          <CardHeader>
            <CardTitle>
              <Text style={styles.sectionTitle}>Weekly Payment Status</Text>
            </CardTitle>
            <CardDescription>
              <Text style={styles.sectionDescription}>
                Track and manage payment status for each week.
              </Text>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <View style={styles.controlRow}>
              <Text style={styles.filterLabel}>Hide paid weeks</Text>
              <Switch
                value={hidePaidWeeks}
                onValueChange={setHidePaidWeeks}
                thumbColor={hidePaidWeeks ? colors.success[700] : colors.ink.paper}
                trackColor={{ false: colors.neutral[300], true: colors.success[200] }}
              />
            </View>

            <View style={styles.table}>
              <View style={[styles.tableHeader, isCompact && styles.tableHeaderCompact]}>
                <Text style={[styles.tableHeaderText, styles.weekColumn]}>Round / Week</Text>
                <Text style={[styles.tableHeaderText, styles.statusColumn]}>Status</Text>
                <Text style={[styles.tableHeaderText, styles.actionColumn]}>Update</Text>
              </View>

              {visibleWeeks.map((week) => {
                const weekStatus = weekStatusMap.get(week.id);
                const leaderInfo = weeklyLeaderMap.get(week.id);
                const completed = isWeekCompleted(week);
                return (
                  <View key={week.id} style={[styles.tableRow, isCompact && styles.tableRowCompact]}>
                    <View style={[styles.weekColumn, isCompact && styles.tableColumnCompact]}>
                      <Text style={styles.weekNumber}>{week.displayLabel || week.label}</Text>
                      <Text style={styles.weekDates}>
                        {week.startsAt ? format(new Date(week.startsAt), 'MM/dd/yy') : 'TBD'} -{' '}
                        {week.endsAt ? format(new Date(week.endsAt), 'MM/dd/yy') : 'TBD'}
                      </Text>
                      {completed && (
                        <View style={styles.weekLeaderContainer}>
                          {leaderInfo?.topPaidMember ? (
                            <Text style={styles.weekLeaderText}>
                              Top Paid:{' '}
                              <Text style={styles.weekLeaderHighlight}>
                                {formatLeaderName(leaderInfo.topPaidMember)}
                              </Text>{' '}
                              ({leaderInfo.topPaidMember.points.toFixed(1)} pts)
                            </Text>
                          ) : (
                            <Text style={styles.weekLeaderText}>Top Paid: No eligible member</Text>
                          )}
                          {leaderInfo?.topMember && leaderInfo.topMember.buyInStatus !== 'paid' && (
                            <Text style={styles.weekLeaderNote}>
                              Overall winner {formatLeaderName(leaderInfo.topMember)} is not paid
                            </Text>
                          )}
                        </View>
                      )}
                    </View>

                    {isCompact && <Text style={styles.compactFieldLabel}>STATUS</Text>}
                    <View style={[styles.statusColumn, isCompact && styles.statusColumnCompact]}>
                      {getWeekStatusBadge(weekStatus || 'unpaid')}
                    </View>

                    {isCompact && <Text style={styles.compactFieldLabel}>UPDATE STATUS</Text>}
                    <View style={[styles.actionColumn, isCompact && styles.actionColumnCompact]}>
                      <AppSelect
                        value={weekStatus || 'unpaid'}
                        onChange={(status) =>
                          updateWeekStatusMutation.mutate({
                            weekId: week.id,
                            status: status as 'paid' | 'unpaid',
                          })
                        }
                        options={[
                          { value: 'paid', label: 'Paid' },
                          { value: 'unpaid', label: 'Unpaid' },
                        ]}
                      />
                    </View>
                  </View>
                );
              })}
              {visibleWeeks.length === 0 && (
                <View style={styles.emptyState}>
                  <Text style={styles.hiddenMessage}>
                    {payoutWeekIds.size === 0 ? 'No weekly payouts configured.' : 'All paid weeks are hidden.'}
                  </Text>
                </View>
              )}
            </View>
          </CardContent>
        </Card>
      ) : null}

      {showSeasonPayout ? (
        <Card style={styles.section}>
          <CardHeader>
            <CardTitle>
              <Text style={styles.sectionTitle}>Season Payout Status</Text>
            </CardTitle>
            <CardDescription>
              <Text style={styles.sectionDescription}>
                Manage the full season payout status.
              </Text>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={[styles.tableHeaderText, styles.weekColumn]}>Season</Text>
                <Text style={[styles.tableHeaderText, styles.statusColumn]}>Status</Text>
                <Text style={[styles.tableHeaderText, styles.actionColumn]}>Update</Text>
              </View>
              <View style={[styles.tableRow, isCompact && styles.tableRowCompact]}>
                <View style={[styles.weekColumn, isCompact && styles.tableColumnCompact]}>
                  <Text style={styles.weekNumber}>Season Payout</Text>
                  <Text style={styles.weekDates}>Full season payout status</Text>
                  {paymentSummary?.seasonLeader?.topMember ? (
                    <View style={styles.weekLeaderContainer}>
                      <Text style={styles.weekLeaderText}>
                        Season Winner:{' '}
                        <Text style={styles.weekLeaderHighlight}>
                          {formatLeaderName(paymentSummary.seasonLeader.topMember)}
                        </Text>{' '}
                        ({paymentSummary.seasonLeader.topMember.points.toFixed(1)} pts)
                      </Text>
                      {paymentSummary.seasonLeader.topPaidMember &&
                        paymentSummary.seasonLeader.topPaidMember.userId !==
                          paymentSummary.seasonLeader.topMember.userId && (
                          <Text style={styles.weekLeaderNote}>
                            Top paid: {formatLeaderName(paymentSummary.seasonLeader.topPaidMember)}
                          </Text>
                        )}
                    </View>
                  ) : null}
                </View>
                {isCompact && <Text style={styles.compactFieldLabel}>STATUS</Text>}
                <View style={[styles.statusColumn, isCompact && styles.statusColumnCompact]}>
                  {getWeekStatusBadge(paymentSummary?.seasonPayoutStatus ?? 'not-set')}
                </View>
                {isCompact && <Text style={styles.compactFieldLabel}>UPDATE STATUS</Text>}
                <View style={[styles.actionColumn, isCompact && styles.actionColumnCompact]}>
                  <AppSelect
                    value={paymentSummary?.seasonPayoutStatus ?? 'unpaid'}
                    onChange={(status) =>
                      updateSeasonStatusMutation.mutate({ status: status as 'paid' | 'unpaid' })
                    }
                    options={[
                      { value: 'paid', label: 'Paid' },
                      { value: 'unpaid', label: 'Unpaid' },
                    ]}
                  />
                </View>
              </View>
            </View>
          </CardContent>
        </Card>
      ) : null}
    </ScrollView>
  );
}

const styles = StyleSheet.create(() => ({
  actionColumn: {
    flex: 1.5,
  },
  actionColumnCompact: {
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 1,
    width: '100%',
  },
  arrowButton: {
    alignItems: 'center',
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    height: 28,
    justifyContent: 'center',
    width: 28,
  },
  arrowButtonText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 16,
    fontWeight: '600',
  },
  badgeContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 4,
  },
  badgeIcon: {
    marginRight: 4,
  },
  badgeTextOptedOut: {
    color: colors.text.secondary,
  },
  badgeTextPaid: {
    color: colors.success[700],
  },
  badgeTextUnpaid: {
    color: colors.text.inverse,
  },
  cardContent: {
    padding: 16,
  },
  cardHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  cardLabel: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  cardSubtext: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 10,
  },
  cardValue: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 24,
    fontWeight: '900',
    marginBottom: 4,
  },
  compactFieldLabel: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  container: {
    flex: 1,
    padding: 16,
  },
  controlRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  emptyState: {
    padding: 16,
  },
  filterLabel: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 14,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    marginBottom: 24,
  },
  hiddenMessage: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.body,
    fontSize: 14,
    paddingVertical: 12,
    textAlign: 'center',
  },
  loadingContainer: {
    padding: 16,
  },
  memberColumn: {
    flex: 2,
  },
  memberEmail: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
  },
  memberName: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.bodyMedium,
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 2,
  },
  section: {
    marginBottom: 24,
  },
  sectionDescription: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.body,
    fontSize: 14,
  },
  sectionHeaderRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: 12,
  },
  sectionHeaderText: {
    flex: 1,
  },
  sectionTitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 18,
    fontWeight: '900',
    marginBottom: 8,
  },
  skeletonCard: {
    flex: 1,
    height: 100,
  },
  skeletonCards: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  skeletonContent: {
    height: 300,
  },
  skeletonTitle: {
    height: 32,
    marginBottom: 16,
    width: '40%',
  },
  statusColumn: {
    alignItems: 'center',
    flex: 1,
  },
  statusColumnCompact: {
    alignItems: 'flex-start',
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 1,
    width: '100%',
  },
  successBadge: {
    backgroundColor: colors.success[100],
  },
  summaryCard: {
    flex: 1,
  },
  summaryCardCompact: {
    flexBasis: '47%',
    minWidth: 150,
  },
  summaryCards: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 24,
  },
  summaryCardsCompact: {
    flexWrap: 'wrap',
  },
  table: {
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    overflow: 'hidden',
  },
  tableColumnCompact: {
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 1,
    width: '100%',
  },
  tableHeader: {
    backgroundColor: colors.ink.paperDark,
    borderBottomColor: colors.ink.press,
    borderBottomWidth: 2,
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  tableHeaderCompact: {
    display: 'none',
  },
  tableHeaderText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  tableRow: {
    alignItems: 'center',
    borderBottomColor: colors.neutral[200],
    borderBottomWidth: 1,
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  tableRowCompact: {
    alignItems: 'stretch',
    flexDirection: 'column',
    gap: 8,
  },
  title: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 24,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  titleCompact: {
    flex: 1,
    fontSize: 18,
  },
  weekColumn: {
    flex: 2,
  },
  weekDates: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
  },
  weekLeaderContainer: {
    marginTop: 6,
  },
  weekLeaderHighlight: {
    color: colors.text.primary,
    fontWeight: '600',
  },
  weekLeaderNote: {
    color: colors.error[600],
    fontSize: 11,
    marginTop: 2,
  },
  weekLeaderText: {
    color: colors.text.secondary,
    fontSize: 12,
  },
  weekNumber: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.bodyMedium,
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 2,
  },
}));
